module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chno"
  }, [_c('a', {
    attrs: {
      "href": "#/Text/NSBT_BibTheol_contents"
    }
  }, [_vm._v("1")])]), _vm._v(" "), _c('h1', {
    staticClass: "chtit",
    attrs: {
      "id": "sigil_toc_id_1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Text/NSBT_BibTheol_contents"
    }
  }, [_vm._v("Introduction")])]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("How did the apostles do biblical theology, and what can we learn from them? The goal of this book is to address these questions.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("There has been a growing interest in the practice of biblical theology among evangelical Christians in the West, an interest seen in the very existence of this series of books. And even many laypeople are now familiar with the idea that the Bible is not only a collection of individual stories but also contains a larger story that stretches from the garden to the new creation.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("From one perspective this is a new phenomenon in the church. The discipline of biblical theology was originally given a home in the academy through the work of post-Enlightenment historical critics who made a distinction between the theology of the Bible (biblical theology) and the theology of the church (dogmatic theology)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-1",
      "id": "FR_Ch01-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1")])]), _vm._v(" This modern, dogged interest in the historical development of the theology of the Bible was then baptized by orthodox theologians who used the new method to analyse the history of redemptive revelation."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-2",
      "id": "FR_Ch01-2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("From another perspective, however, recounting the theological teaching of the Bible as a story with historical development was certainly a part of pre-Enlightenment theologizing as well. Perhaps the second-century Christian bishop Irenaeus is most famous for taking this approach."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-3",
      "id": "FR_Ch01-3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")])]), _vm._v(" And yet long before Irenaeus Jewish interpreters of the Bible summarized their understanding of holy writ in narrative historical form. Such summaries are found both within the OT (e.g. Neh. 9; Pss 105; 106; etc.) and outside it (e.g. Sir. 44–50). Our interest in this book, however, is not in these pre-Christian summaries, but rather in the summaries of Israel’s story composed after the coming of Jesus Christ in the apostolic witness of the NT.")]), _vm._v(" "), _c('h3', {
    staticClass: "b_head",
    attrs: {
      "id": "sigil_toc_id_2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Text/NSBT_BibTheol_contents"
    }
  }, [_vm._v("Our idea: exploring the exposed iceberg")])]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("In the last century of biblical scholarship and especially in the last few decades the use of the OT in the NT has been explored at great length. For a time there was an intense interest in parallels between NT exegesis and the uses of the OT found at Qumran (pesher) or those found in the later rabbis (midrash)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-4",
      "id": "FR_Ch01-4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")])]), _vm._v(" More recently there has been an interest in literary studies that consider factors behind the text such as the subtler echoes of the OT in the NT, ways that the OT story shapes the Gospels, or OT narratives that may lie underneath Paul’s arguments."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-5",
      "id": "FR_Ch01-5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("If we use the metaphor of an iceberg, many of these studies have been deep-sea explorations attempting to shine a spotlight on things that are submersed in dark and frigid places, such as Paul’s Pharisaic training (midrash) or the philosophical echo chamber in which ancient texts find new resonations (intertextuality). We have all benefited from these studies but our project takes a more direct approach. Our goal is to explore those parts of the iceberg that are standing in plain sight under the light of the sun. Sometimes the most difficult thing to observe carefully is the thing that you see all the time, such as the famous ‘hall of faith’ in Hebrews 11. And yet Hebrews 11 is not only one of the most important examples of the use of the OT in the NT, but is also an inspired example of how to put the story of the Bible together.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("The iceberg metaphor can also be usefully applied to our goal of understanding the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("biblical theology")]), _vm._v(" of the apostles by means of these passages. One difficulty in this discipline is that no one agrees about what exactly ‘biblical theology’ is. A recent volume entitled "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Understanding Biblical Theology ")]), _vm._v("finds no fewer than five different approaches represented by modern scholars (Klink and Lockett 2012). These approaches range from those who view the discipline as merely a descriptive, historical discipline, unearthing the religion of the human authors of the Bible,"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-6",
      "id": "FR_Ch01-6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")])]), _vm._v(" to those who argue that it should fully embrace the same categories and methods as systematic theology."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-7",
      "id": "FR_Ch01-7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7")])]), _vm._v(" This is why on a practical level the term ‘biblical theology’ may describe the exploration of one biblical author such as Paul (Schreiner 2001), of a larger corpus such as the OT (Dempster 2003), of the development of the OT in the NT (Beale 2011), of the central theme of the entire Bible (Hamilton 2010)"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-8",
      "id": "FR_Ch01-8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")])]), _vm._v(" or even of a view of God that accords with the Bible."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-9",
      "id": "FR_Ch01-9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Thus, an exploration of the ‘biblical theology’ of the apostles could in one sense be a study of everything the NT authors say and especially what they say about their Bible, the OT. Our goal in this project is not to explore the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("entire ")]), _vm._v("biblical theology of the apostles but to explore the exposed tip of the iceberg, their explicit summaries of Israel’s history. While there are many different approaches to the discipline of biblical theology, one characteristic of most of them is the attempt to come to terms with the post-Enlightenment focus on the Bible as a historical book. The passages in the NT that have the most bearing on this kind of study are those that narrate the history recounted in the OT from Abraham to the exile and beyond. These summaries are not everything the NT authors have to say about biblical theology, but are the clearest examples of apostolic reflection on the history or story of the Bible. Moreover, studying the exposed part of the iceberg will help us better understand what lies below the surface.")]), _vm._v(" "), _c('h3', {
    staticClass: "b_head",
    attrs: {
      "id": "sigil_toc_id_3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Text/NSBT_BibTheol_contents"
    }
  }, [_vm._v("Our criteria: New Testament summaries of Israel’s story")])]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("Movement in this direction has already begun in the recent article of Hood and Emerson, who attempt to isolate ‘summaries of Israel’s story’ (SIS) as a compositional category. This article reviews modern research in this ‘genre-transcending literary category’,"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-10",
      "id": "FR_Ch01-10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")])]), _vm._v(" presents criteria to identify the category SIS and then gives a provisional list of about seventy to eighty such summaries in Jewish and Christian literature found both inside and outside the Bible.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Hood and Emerson trace the beginning of their history of research to the publication of Ethelbert Stauffer’s "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("New Testament Theology ")]), _vm._v("in the mid-twentieth century that contained in an appendix ‘an apparently unprecedented list’ of 121 ‘theological summaries of history’."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-11",
      "id": "FR_Ch01-11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("11")])]), _vm._v(" Stauffer perceived in these summaries support for his own view of biblical theology, which is rooted in the sacred history:")]), _vm._v(" "), _c('p', {
    staticClass: "dpy_pros1s"
  }, [_vm._v("The theology of the early church was a process of ordering. What went on was not the making of metaphysical concepts, nor yet the construction of a system, but an ordering of thought, that sought to discover the actual relationships between the different elements of the world of human experience. The answer that was found as a result of the search was like this: God ordered all reality in history . . . In this sense the theology of history is the primary and canonical form of Christian thinking . . ."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-12",
      "id": "FR_Ch01-12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi_space"
  }, [_vm._v("Hood and Emerson next survey several other scholars after Stauffer who have studied these summaries. They observe however that")]), _vm._v(" "), _c('p', {
    staticClass: "dpy_pros1s"
  }, [_vm._v("none of these scholars address these summaries as a distinct compositional category as Stauffer did . . . Scholars usually focus on one or a handful of closely related forms [e.g. those that follow the ‘deuteronomic’ sin–exile–return view of history] rather than giving attention to summaries as a form-transcendent phenomenon."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-13",
      "id": "FR_Ch01-13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("13")])])]), _vm._v(" "), _c('p', {
    staticClass: "fo_space"
  }, [_vm._v("They also survey scholars associated with the ‘narrative turn’ in biblical scholarship (e.g. N. T. Wright) who have brought attention back to Israel’s story but do not cite Stauffer and his list of summaries."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-14",
      "id": "FR_Ch01-14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Joachim Jeska, influenced by Stauffer, has given ‘the fullest review and the sole attempt in the extant literature to establish a technical term for this phenomenon, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Summarien der Geschichte Israels")]), _vm._v("’."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-15",
      "id": "FR_Ch01-15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("15")])]), _vm._v(" He views these summaries not merely as ‘historical reporting, but attempts to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("actualize ")]), _vm._v("Israel’s history, bringing it to bear for present or future purposes, sometimes implicitly by the creation of patterns, sometimes by explicit comment’."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-16",
      "id": "FR_Ch01-16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("16")])]), _vm._v(" He comes up with a much shorter list than Stauffer (twenty-seven examples), partly because he does not use the shorter texts and partly because he restricts his investigation to ancient Jewish summaries that tell the story from Genesis to 2 Kings."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-17",
      "id": "FR_Ch01-17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("17")])]), _vm._v(" Hood and Emerson observe that, although Jeska arranges his lists according to genre, he ‘follows Stauffer in accumulating examples from a variety of forms and genres’,"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-18",
      "id": "FR_Ch01-18",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("18")])]), _vm._v(" treating these summaries as a compositional category that transcends genre and can be examined in their own right.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("In the final section of their article Hood and Emerson suggest various criteria by which they delineate the compositional category of SIS. It should be observed that they purposely use the English word ‘story’ rather than ‘history’, not to deny the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("realis ")]), _vm._v("nature of Israel’s narrative, but to describe a narrative that moves beyond the past into the present and even the future."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-19",
      "id": "FR_Ch01-19",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("19")])]), _vm._v(" They first identify SIS as a ‘shorter version of the phenomenon known as rewritten Bible’ (RB), for example, the retelling of Genesis and Exodus in "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Jubilees")]), _vm._v(" or Josephus’s longer history of the Jewish people ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Jewish Antiquities")]), _vm._v(")."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-20",
      "id": "FR_Ch01-20",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("20")])]), _vm._v(" The difference between this genre of literature and the compositional category of SIS is that an SIS is ‘highly condensed’ rather than being a lengthy summary and is always found ‘"), _c('em', {
    staticClass: "italic"
  }, [_vm._v("within ")]), _vm._v("a work’ rather than standing on its own."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-21",
      "id": "FR_Ch01-21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Next they suggest several criteria, which can be summarized with the following: an SIS recounts the ‘characters, events, and institutions’ of Israel’s history in ‘chronological order’ and at ‘substantial length’."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-22",
      "id": "FR_Ch01-22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("22")])]), _vm._v(" By ‘substantial length’ they are attempting to eliminate briefer references to Israel’s history such as Jesus’ statement that ‘many will come from the east and the west, and will take their places at the feast with Abraham, Isaac and Jacob in the kingdom of heaven’ (Matt. 8:11). By ‘chronological order’, Hood and Emerson refer to a passage that ‘moves in proper “historical” direction’, allowing for ‘minor chronological digression’ such as in Hebrews 11."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-23",
      "id": "FR_Ch01-23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("23")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("With these criteria in place, Hood and Emerson write out their list of Jewish and Christian SIS. Since our concern is with the apostolic witness to Israel’s story, we will list only the seven summaries they find in the NT: Matthew 1:1–17; Mark 12:1–23//Matthew 21:33–46//Luke 20:9–18; Acts 7; Acts 13:16–41; Romans 9 – 11; Hebrews 11:1 – 12:2; and Revelation 12:1–12.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Our study of apostolic biblical theology follows Hood and Emerson’s criteria exactly, although our list of summaries is slightly different. We have selected the passages in the NT that recount the characters, events and institutions of Israel’s story in chronological order and at substantial length. Following these criteria we have excluded many texts that recount elements of Israel’s story but not in chronological order or at substantial length."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-24",
      "id": "FR_Ch01-24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("24")])]), _vm._v(" One thinks of the brief recounting of certain episodes in the Gospels such as Jonah and the huge fish or Solomon and the Queen of the South (Matt. 12:39–42), Peter’s sermons in the early chapters in Acts (Acts 2:14–36; 3:12–26), or even the more substantial reflections of Paul on Abraham (Rom. 4) and Adam (Rom. 5:12–21; 1 Cor. 15) that do not show chronological or historical movement. In other words, while these recountings appeal to characters or events from the story of Israel, they do not really tell that story.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("The application of Hood and Emerson’s criteria, of course, involves a measure of subjectivity. In fact, upon reflection we decided to exclude one of their NT examples of SIS (Rev. 12:1–12) and include another that they do not (Gal. 3 – 4). In the former case we were convinced that Revelation 12:1–12 is really about only a part of the biblical story – the defeat of the devil and his persecution of the church – rather than a recounting of the entire story of Israel."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-25",
      "id": "FR_Ch01-25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("25")])]), _vm._v(" And in the latter case we saw all the earmarks of an SIS: Israel’s characters, events and institutions in chronological order, at substantial length and in summary form. Galatians 3 – 4 names Abraham, Hagar, Sarah, Ishmael, Isaac, Moses and Jesus Christ, recounts events in the life of Abraham, the giving of the law and God’s sending of his Son and Spirit, and elucidates the significance of one Jewish institution at length – the law – along with some tantalizingly brief comments about the city of Jerusalem.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Some readers may question our inclusion of certain passages. For example, Snodgrass argues that the parable of the tenants is not exactly a story of Israel. ‘It is not precise to describe the parable as a graphic presentation of the course of salvation-history since this would require a much fuller treatment similar to that in Acts 7.’"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-26",
      "id": "FR_Ch01-26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("26")])]), _vm._v(" While we concede that this retelling of Israel’s story is more allusive and poetic than Stephen’s speech in Acts 7 or even Matthew’s genealogy, it nonetheless can be seen as a story of Israel’s history. If indeed the vineyard in some way represents Israel, then we have a story of Israel’s founding and their failure. On the other hand, some readers may be disappointed that we excluded other passages. For example, we could see someone making a good argument for Romans 5:12–21, which begins with Adam, refers to Moses and the coming of the law and ends with the reign of grace through Christ. However, in our view this passage is fundamentally a typological comparison between Adam and Christ and does not attempt to show the historical progression of Israel’s story.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("At the end of the day, however, we had to draw the line somewhere. Our goal once again was not to analyse every passage pertaining to the apostles’ biblical theology but to explore the exposed parts of the iceberg; that is, the NT SIS. While not all may agree about which parts of the iceberg are exposed, we believe our selections provide a representative picture of the biblical theology of the earliest Christians. Even if we have missed some of that territory, we hope to expose our readers to enough of it to be able to follow the major plot points of Israel’s story and see a glimpse into how the apostles put their Bible together.")]), _vm._v(" "), _c('h3', {
    staticClass: "b_head",
    attrs: {
      "id": "sigil_toc_id_4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Text/NSBT_BibTheol_contents"
    }
  }, [_vm._v("Our method: context, content and contribution")])]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("Guided by Hood and Emerson’s criteria, we have identified seven SIS in the NT: (1) the genealogy in Matthew (Matt. 1:1–17); (2) Jesus’ parable of the tenants (Matt. 21:33–46; etc.); (3) Stephen’s speech (Acts 7); (4) Paul’s sermon in Pisidian Antioch (Acts 13:16–41); (5) Paul’s argument from salvation history in Galatians (Gal. 3 – 4); (6) his defence of God’s faithfulness to Israel in Romans (Rom. 9 – 11); and (7) the author of Hebrews’s exhortation to his readers about persevering faith (Heb. 11). A chapter will be given to each NT book that includes these summaries. For Matthew and Acts we will address the two summaries in each of these books together to some degree. While we recognize them as distinct SIS, we must also recognize Matthew’s and Luke’s respective agendas in including two stories in their composition and so treat them accordingly.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Our method of analysis in each chapter will follow a similar pattern, even though there will be some differences due to the genre in which the SIS is placed and the unique content of each summary. Each chapter will first examine the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("context ")]), _vm._v("of the story. Our goal here is to place each SIS in the context of its individual book and to understand "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("why ")]), _vm._v("this author has told Israel’s story. The SIS in the NT and beyond are not merely recounting Israel’s story for historical interest but telling the story for specific reasons. If we can identify these reasons (i.e. the function the author intends the summary to serve), then it will help us to see how he understands the story and what he expects readers to take away. We do not mean to suggest here that SIS are merely a function of the author’s rhetorical goals – that he skews the story to fit these goals – but rather to see the main thing the author notices in the OT story of Israel and chooses to highlight for a specific situation."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/NSBT_BibTheol_notes#FN_Ch01-27",
      "id": "FR_Ch01-27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Second, we will examine the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("content ")]), _vm._v("of each story. "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("How ")]), _vm._v("does the author tell the story in order to accomplish his rhetorical goals? SIS are by their nature highly condensed and therefore truncated accounts of Israel’s history. Which parts of the story does the author highlight and what exactly does he say about these parts? This section will give a careful exegetical analysis of each summary and thus will be the longest and most important part of each chapter.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Finally, we will reflect on the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("contribution ")]), _vm._v("that each story makes to biblical theology. Once we have done the preliminary groundwork of analysing "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("why ")]), _vm._v("the author recounts Israel’s story and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("how ")]), _vm._v("he recounts that story, we will be in a position to understand "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("what ")]), _vm._v("the author’s exact contribution to biblical theology is. This section will attempt to skim off the top what we have learned from our analysis of each NT SIS.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("In the next chapter, ‘Matthew and Israel’s continuing story’, we will explore how Matthew relates the story of Jesus to the story of Israel in the evangelist’s opening genealogy and in the parable of the tenants. Chapter 3, ‘Luke, Acts and the climax of Israel’s story’, will examine the covenantal structure of Israel’s story as recorded in Stephen’s speech and Paul’s sermon in Pisidian Antioch. Chapter 4, ‘Galatians and Israel’s law’, will examine how the story of Israel proves that the inheritance does not come by the law but by the Abrahamic promise. Chapter 5, ‘Romans and Israel’s identity’, will highlight Paul’s nuanced definition of Israel’s identity in his defence of God’s faithfulness. Chapter 6, ‘Hebrews and Israel’s inheritance’, will explore how the author of Hebrews uses the nature of Israel’s inheritance to sustain his audience’s faith. Finally, in chapter 7, we will summarize our observations and draw out implications. In the main body of the book our reflection on the contributions of each summary will only be descriptive. Then, in chapter 7, we will offer some prescriptive points about how apostolic biblical theology should guide our own practice of biblical theology. This after all is our ultimate goal – not only to understand how the apostles did biblical theology, but to understand what we can (and should) learn from them.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-0ef8758a","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/Text/NSBT_BibTheol_ch01.vue
// module id = 78
// module chunks = 0